import { buildLoginRedirect, publicPage, type PageAuthContext } from '../lib/page-runtime';
import type { SkillListItem } from '../../../src/lib/ui/skill-card';
import type { FlywheelHighlights } from '../../../src/lib/ui/skill-flywheel';

type SkillsPageBootstrap = {
  readonly name: 'skills';
  readonly search?: string;
  readonly sort?: string;
  readonly total?: number;
  readonly items?: readonly SkillListItem[];
  readonly highlights?: FlywheelHighlights | null;
};

function getBootstrap(): SkillsPageBootstrap | null {
  const page = window.__SKILLFAB_PAGE__;
  if (!page || page.name !== 'skills') return null;
  return page as SkillsPageBootstrap;
}

function validSort(value: string | undefined): 'downloads' | 'recent' | 'name' {
  if (value === 'recent' || value === 'name') return value;
  return 'downloads';
}

const requestSkillPath = '/issues?new=1';

function syncRequestSkillLinks(ctx: PageAuthContext): void {
  const href = ctx.loggedIn ? requestSkillPath : buildLoginRedirect(requestSkillPath);
  document.querySelectorAll<HTMLAnchorElement>('[data-request-skill-link]').forEach(function(link) {
    link.href = href;
  });
}

publicPage({
  onAuth: syncRequestSkillLinks,
  init: async function(ctx) {
    const page = getBootstrap();
    if (!page) return;
    syncRequestSkillLinks(ctx);

    const searchInput = document.getElementById('skills-search') as HTMLInputElement | null;
    const sortSelect = document.getElementById('skills-sort') as HTMLSelectElement | null;
    const grid = document.getElementById('skills-grid');
    const countEl = document.getElementById('skills-count');
    const highlightsEl = document.getElementById('skills-flywheel-highlights');
    if (!searchInput || !sortSelect || !grid || !countEl) return;
    const searchEl = searchInput;
    const sortEl = sortSelect;
    const gridEl = grid;
    const countElement = countEl;

    let debounceTimer: number | undefined;
    let requestSeq = 0;

    function render(items: readonly SkillListItem[], total: number): void {
      countElement.textContent = 'Total: ' + total;
      gridEl.innerHTML = window.SkillsPageRenderers.renderGrid(items);
    }

    function setLoading(loading: boolean): void {
      gridEl.style.opacity = loading ? '0.5' : '1';
      searchEl.disabled = loading;
      sortEl.disabled = loading;
    }

    async function fetchSkills(): Promise<void> {
      const search = searchEl.value.trim();
      const sort = validSort(sortEl.value);
      const seq = ++requestSeq;

      const url = new URL(window.location.href);
      search ? url.searchParams.set('search', search) : url.searchParams.delete('search');
      url.searchParams.set('sort', sort);
      window.history.replaceState({}, '', url.toString());

      setLoading(true);
      const result = await window.SkillsPageData.searchSkills({
        search: search || undefined,
        sort,
        limit: 60,
      });
      if (seq !== requestSeq) return;

      if (!result.ok) {
        gridEl.innerHTML = '<div class="col-span-full card p-4 text-center text-red-400">Failed to load skills.</div>';
        countElement.textContent = 'Total: 0';
        setLoading(false);
        return;
      }

      render(result.data.items, result.data.total);
      setLoading(false);
    }

    render(page.items || [], page.total || 0);

    if (highlightsEl && !page.highlights) {
      window.SkillsPageData.loadFlywheelHighlights().then((result) => {
        if (result.ok) {
          highlightsEl.outerHTML = window.SkillsPageRenderers.renderHighlights(result.data);
        }
      }).catch(() => {
        highlightsEl.outerHTML = window.SkillsPageRenderers.renderHighlights(null);
      });
    }

    searchEl.addEventListener('input', function() {
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        void fetchSkills();
      }, 300);
    });

    sortEl.addEventListener('change', function() {
      void fetchSkills();
    });
  },
});
